import DOMPurify from "dompurify";
import { FileText, File as FileIcon, Download } from "lucide-react";
import { mediaUrl } from "@/lib/api";

const CLEAN = (html) => DOMPurify.sanitize(html || "", {
  ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "ul", "ol", "li", "a", "span"],
  ALLOWED_ATTR: ["href", "target", "rel", "class", "data-user-id"],
});

const linkify = (text) => {
  const parts = (text || "").split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p)
      ? <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline break-all">{p}</a>
      : <span key={i}>{p}</span>);
};

/** Renders a note body: sanitized rich HTML when present, else plain text. */
export function NoteBody({ note }) {
  if (note?.content_html) {
    return <div className="rte-content text-sm text-zinc-200 break-words" data-testid="note-body-html"
      dangerouslySetInnerHTML={{ __html: CLEAN(note.content_html) }} />;
  }
  return <div className="text-sm text-zinc-200 whitespace-pre-wrap break-words" data-testid="note-body-text">{linkify(note?.content || "")}</div>;
}

/** Renders a note's attachments: image thumbnails + file chips. */
export function NoteAttachments({ attachments = [], onImageClick }) {
  if (!attachments?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="note-attachments">
      {attachments.map((a) => a.is_image ? (
        <button key={a.file_id} type="button" onClick={() => onImageClick?.(a)} data-testid={`note-att-img-${a.file_id}`}
          className="block rounded-lg overflow-hidden border border-zinc-800 hover:border-indigo-500/60 transition-colors">
          <img src={mediaUrl(a.url)} alt={a.filename} className="h-20 w-20 object-cover" loading="lazy" />
        </button>
      ) : (
        <a key={a.file_id} href={mediaUrl(a.url)} target="_blank" rel="noopener noreferrer" data-testid={`note-att-file-${a.file_id}`}
          className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-zinc-800 bg-zinc-900 hover:border-zinc-700 transition-colors max-w-[220px]">
          {a.content_type === "application/pdf" ? <FileText className="h-4 w-4 text-red-400 flex-shrink-0" /> : <FileIcon className="h-4 w-4 text-zinc-400 flex-shrink-0" />}
          <span className="text-xs text-zinc-300 truncate">{a.filename}</span>
          <Download className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
        </a>
      ))}
    </div>
  );
}
